//Check for Poppers
import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { usePopper } from 'react-popper';

import usePosition from '@hooks/usePosition';
// UIKit Components
import { Box, Portal } from '@spazious/react-components';
import { styled } from '@spazious/react-styles';

import DurationField, { DurationFieldProps } from '../DurationField/index.js';
import DurationSelector, { DurationSelectorProps } from '../DurationSelector/index.js';
dayjs.extend(relativeTime);

export const PopperArrowCorner = styled(
  (props: { arrowSize?: number; style?: React.CSSProperties }) => <Box {...props} />
).withConfig({
  shouldForwardProp: (prop) => !['arrowSize'].includes(prop),
  displayName: 'PopperArrowCorner',
})`
  position: absolute;
  background-color: var(--background-color);
  width: ${({ arrowSize }: any) => arrowSize}px;
  height: ${({ arrowSize }: any) => arrowSize}px;
  transform: rotate(45deg);
`;
//[colorchange]
export const PaperBox = styled(Box)`
  background-color: var(--background-color);
  box-shadow: rgba(0, 0, 0, 0.12) 0px 14px 28px 0px, rgba(0, 0, 0, 0.24) 0px 10px 10px 0px;
  border-radius: 0.5rem;
  padding: 16px;
  @media (max-width: 425px) {
    padding: 12px;
  }
`;
export type PopperArrowProps = React.HTMLAttributes<HTMLDivElement> & {
  placement?: 'top' | 'bottom' | 'left' | 'right';
  referenceHidden?: boolean;
  arrowSize?: number;
  cornerStyle?: React.CSSProperties;
};

export const PopperArrow = styled(
  React.forwardRef<HTMLDivElement, PopperArrowProps>(
    ({ arrowSize, cornerStyle, ...restProps }, ref) => {
      return (
        <Box ref={ref} {...restProps}>
          <PopperArrowCorner arrowSize={arrowSize} style={cornerStyle} />
        </Box>
      );
    }
  )
).withConfig({
  shouldForwardProp: (prop) => !['placement', 'referenceHidden', 'escaped'].includes(prop),
  displayName: 'PopperArrow',
})`
  position: absolute;
  ${({ placement, arrowSize = 10 }) => {
    if (!placement) return '';
    const offset = arrowSize * 0.5;
    return `${placement}: ${placement === 'top' ? `-${offset}px` : `${offset}px`};`;
  }}
  visibility: ${({ referenceHidden = false }) => (referenceHidden ? 'hidden' : 'visible')};
`;

export const ArrowWrapper: React.FC<{
  arrowRef: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
  arrowSize?: number;
  arrowStyle?: React.CSSProperties;
  arrowProps: PopperArrowProps;
}> = ({ arrowRef, arrowSize, arrowStyle, arrowProps, children }) => {
  return (
    <Box>
      <PopperArrow
        arrowSize={arrowSize}
        cornerStyle={{
          zIndex: -1,
          boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
        }}
        style={{
          ...arrowStyle,
          zIndex: -1,
        }}
        {...arrowProps}
      />
      <Box>{children}</Box>
      <PopperArrow arrowSize={arrowSize} style={arrowStyle} {...arrowProps} ref={arrowRef} />
    </Box>
  );
};

export const parseArrowAttributes = (attrs: any = {}) => ({
  referenceHidden: attrs['data-popper-reference-hidden'] === 'true',
  escaped: attrs['data-popper-escaped'] === 'true',
  placement: attrs['data-popper-placement'] as 'bottom' | 'left' | 'right' | 'top',
});

export type DurationPickerProps<P = any> = Omit<DurationSelectorProps, 'value'> &
  DurationFieldProps<P> & {
    /**
     * When true, the component will be disabled
     */
    disabled?: boolean;
    open?: boolean;
    defaultOpen?: boolean;
    onClose?: () => void;
    onOpen?: () => void;
  };

/**
 * ISO 8601 Durations
 *
 * @example
 * PT12H30M5S
 */
export const DurationPicker = <P,>({
  value,
  hold,
  units,
  unitsProps,
  defaultOpen,
  open: openProp,
  onChange,
  onClose,
  onOpen,
  disabled,
  ...restProps
}: DurationPickerProps<P>): React.ReactElement | null => {
  const [open, setOpen] = useState(defaultOpen);
  const [referenceElement, setReferenceElement] = useState<Element | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const arrowSize = 20;

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [arrowSize, arrowSize],
        },
      },
      {
        name: 'arrow',
        options: {
          element: arrowEl,
        },
      },
    ],
  });

  const handleOpen = useCallback(() => {
    if (!disabled) {
      if (openProp !== undefined) {
        onOpen?.();
      } else {
        setOpen(true);
      }
    }
  }, [disabled, onOpen, openProp]);

  const handleClose = useCallback(() => {
    if (openProp !== undefined) {
      onClose?.();
    } else {
      setOpen(false);
    }
  }, [onClose, openProp]);

  // Handle click outside
  useEffect(() => {
    if (!open && !openProp) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Checking if click is outside both the field and the popper
      const isOutsideField = fieldRef.current && !fieldRef.current.contains(target);
      const isOutsidePopper = popperRef.current && !popperRef.current.contains(target);

      if (isOutsideField && isOutsidePopper) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, openProp, handleClose]);

  const position = usePosition();
  return (
    <>
      <Box ref={fieldRef}>
        <DurationField
          ref={setReferenceElement}
          disabled={disabled}
          units={units}
          value={value}
          onClick={handleOpen}
          onFocus={handleOpen}
          {...(restProps as any)}
        />
      </Box>
      {(open || openProp) && (
        <Portal>
          <Box
            ref={(node) => {
              popperRef.current = node;
              setPopperElement(node);
            }}
            style={{
              ...styles.popper,
              zIndex: 1400,
            }}
            {...attributes.popper}
          >
            <ArrowWrapper
              arrowProps={{
                ...parseArrowAttributes(attributes.arrow),
                placement: position,
              }}
              arrowRef={setArrowEl}
              arrowSize={arrowSize}
              arrowStyle={styles.arrow}
            >
              <PaperBox>
                <DurationSelector
                  disabled={disabled}
                  hold={hold}
                  units={units}
                  unitsProps={unitsProps}
                  value={value ?? 'PT0S'}
                  onChange={onChange}
                />
              </PaperBox>
            </ArrowWrapper>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default DurationPicker;
